import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import { useTheme } from '@mui/material/styles';
import { useSubmenuCollapse } from 'src/hooks/use-submenu-collapse';

import { NavItemCollapsed } from './nav-item-collapsed';
import { SubmenuPanel } from './submenu-panel';
import type { SidebarItem } from './types';
import { useSidebarData } from './use-sidebar-data';

const SIDEBAR_WIDTH_COLLAPSED = 70;

export const Sidebar = () => {
  const { sidebarData, panelItem, expandAllKey } = useSidebarData();
  const theme = useTheme();
  const { isSubmenuCollapsed } = useSubmenuCollapse();

  // Submenu shows only when the current route has one and it isn't collapsed
  const showSubmenu = !!panelItem && !isSubmenuCollapsed;

  // Sidebar is always collapsed (icon-only)
  const isCollapsed = !showSubmenu;

  const renderNavItem = (item: SidebarItem) => (
    <NavItemCollapsed key={item.id} item={item} isPanelOpen={!!panelItem && panelItem.id === item.id} />
  );

  return (
    <Box
      sx={{
        position: 'fixed',
        borderRadius: isCollapsed ? '0 20px 0 0' : 0,
        top: '84px',
        bgcolor: '#fafafa',
        left: 0,
        height: 'calc(100vh - 84px)',
        display: 'none',
        borderRight: isCollapsed ? 0 : `1px solid ${theme.palette.grey[200]}`,
        zIndex: 1101,
        flexDirection: 'column',
        boxShadow: '14px 17px 40px 0px rgba(3, 17, 31, 0.08)',
        // Soften the corner-radius/border swap that happens when the submenu
        // panel opens or closes, in step with the panel's 0.2s slide.
        transition: 'border-radius 0.2s ease-in-out',
        [theme.breakpoints.up('lg')]: {
          display: 'flex',
        },
      }}
    >
      {/* Main Sidebar */}
      <Box
        sx={{
          position: 'relative',
          width: SIDEBAR_WIDTH_COLLAPSED,
          height: '100%',
          // Own background + stacking above the submenu panel, so the panel's
          // slide-out disappears underneath the rail instead of crossing it.
          bgcolor: '#fafafa',
          zIndex: 2,
          borderRadius: 'inherit',
          display: 'flex',
          flexDirection: 'column',
          pt: 2,
          pb: 5,
          px: 0.9,
          transition: 'width 0.2s ease-in-out, padding 0.2s ease-in-out',
          overflow: 'visible',
        }}
      >
        {/* Navigation Sections */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: 1,
            // borderRight: '1px solid',
            // borderColor: 'grey.200',
          }}
        >
          {sidebarData.map(section => (
            <Box key={section.id}>
              <List
                disablePadding
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isCollapsed ? 1.75 : 1.75,
                }}
              >
                {section.items.map(renderNavItem)}
              </List>
              {section.showDivider && (
                <Divider
                  sx={{
                    mt: 2,
                    borderColor: 'grey.300',
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        {/* Social Icons */}
        {/* <SocialIcons links={socialLinks} collapsed={isCollapsed} /> */}
      </Box>
      {/* Submenu Panel — stays mounted so collapsing can animate the slide-out */}
      {panelItem && <SubmenuPanel item={panelItem} open={showSubmenu} expandAllKey={expandAllKey} />}
    </Box>
  );
};

export default Sidebar;
